import React, { useEffect, useRef, useState } from 'react';

interface CompanyLogoProps {
  src: string;
  alt: string;
  className?: string;
}

const REPLAY_INTERVAL_MS = 60000;

const isGifSource = (src: string): boolean =>
  src.toLowerCase().split(/[?#]/)[0].endsWith('.gif');

/**
 * Renders the company logo. If the logo is an animated .gif, the animation is
 * restarted whenever the window regains focus and on a fixed interval. The gif
 * is fetched once as a blob; each replay swaps in a fresh object URL because
 * browsers share the animation timeline for identical cached gif URLs.
 */
const CompanyLogo: React.FC<CompanyLogoProps> = ({ src, alt, className }) => {
  const [displaySrc, setDisplaySrc] = useState(src);
  const blobRef = useRef<Blob | null>(null);
  const objectUrlRef = useRef<string | null>(null);

  useEffect(() => {
    if (!isGifSource(src)) {
      setDisplaySrc(src);
      return;
    }

    let cancelled = false;

    const replay = () => {
      if (document.hidden || !blobRef.current) return;
      if (objectUrlRef.current) URL.revokeObjectURL(objectUrlRef.current);
      objectUrlRef.current = URL.createObjectURL(blobRef.current);
      setDisplaySrc(objectUrlRef.current);
    };

    fetch(src)
      .then((res) => (res.ok ? res.blob() : Promise.reject(new Error(`${res.status}`))))
      .then((blob) => {
        if (cancelled) return;
        blobRef.current = blob;
        replay();
      })
      .catch(() => {
        /* keep the plain src; replays just won't be available */
      });

    const onFocusOrVisible = () => replay();
    window.addEventListener('focus', onFocusOrVisible);
    document.addEventListener('visibilitychange', onFocusOrVisible);
    const intervalID = window.setInterval(replay, REPLAY_INTERVAL_MS);

    return () => {
      cancelled = true;
      window.removeEventListener('focus', onFocusOrVisible);
      document.removeEventListener('visibilitychange', onFocusOrVisible);
      window.clearInterval(intervalID);
      if (objectUrlRef.current) {
        URL.revokeObjectURL(objectUrlRef.current);
        objectUrlRef.current = null;
      }
      blobRef.current = null;
    };
  }, [src]);

  return <img key={displaySrc} src={displaySrc} alt={alt} className={className} />;
};

export default CompanyLogo;
